import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newStationDetailKey, type StationDetailValue } from './stationFormSchema';
import type { StationFormApi } from './useStationForm';

/** Aktivní mapový režim — řídí, na co reaguje klik do mapy (banner nahoře + `onMapClick`). */
export type MapMode =
  | { kind: 'idle' }
  | { kind: 'new-place' }
  | { kind: 'edit-place'; key: string }
  | { kind: 'station-coords' };

/**
 * Editor nástupních míst a mapových režimů detailu stanice. Drží lokální stav (mapový režim, signál
 * vystředění, drag & drop, akordeon) a všechny operace nad polem `stationDetails` formuláře —
 * přidávání, mazání, přeřazení (pořadí = `Ord`, 1. = hlavní = GPS stanice) a zápis souřadnic.
 */
export function usePlaceEditor(form: StationFormApi) {
  const { t } = useTranslation('products');

  const [mapMode, setMapMode] = useState<MapMode>({ kind: 'idle' });
  const [fitSignal, setFitSignal] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Akordeon — rozbalené je vždy nejvýše jedno nástupní místo.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const fit = useCallback(() => setFitSignal((value) => value + 1), []);

  const expand = useCallback((key: string) => {
    setExpandedKey(key);
  }, []);

  const toggleExpand = useCallback((key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  const makePlace = useCallback(
    (
      key: string,
      lat: number | null,
      lng: number | null,
      zoom: number | null
    ): StationDetailValue => ({
      key,
      id: null,
      description: t('stationDetail.defaultName'),
      street: '',
      descriptiveNumber: '',
      orientationNumber: '',
      poBox: '',
      city: '',
      zipCode: '',
      country: null,
      latitude: lat,
      longitude: lng,
      zoom,
    }),
    [t]
  );

  const clearStationCoords = useCallback(() => {
    form.setFieldValue('stationLatitude', null);
    form.setFieldValue('stationLongitude', null);
    form.setFieldValue('stationZoom', null);
  }, [form]);

  // ── GPS / mapové režimy ────────────────────────────────────────────────────────────────────
  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      if (mapMode.kind === 'new-place') {
        const key = newStationDetailKey();
        form.setFieldValue('stationDetails', (prev) => [...prev, makePlace(key, lat, lng, 13)]);
        clearStationCoords(); // §6.3 — 1. místo přebírá souřadnice, stanice je zahodí
        expand(key);
        setMapMode({ kind: 'idle' });
        return;
      }
      if (mapMode.kind === 'edit-place') {
        const index = form.state.values.stationDetails.findIndex((d) => d.key === mapMode.key);
        if (index >= 0) {
          form.setFieldValue(`stationDetails[${index}].latitude`, lat);
          form.setFieldValue(`stationDetails[${index}].longitude`, lng);
        }
        setMapMode({ kind: 'idle' });
        return;
      }
      if (mapMode.kind === 'station-coords') {
        form.setFieldValue('stationLatitude', lat);
        form.setFieldValue('stationLongitude', lng);
        form.setFieldValue('stationZoom', (prev) => prev ?? 13);
        setMapMode({ kind: 'idle' });
      }
    },
    [mapMode, form, makePlace, clearStationCoords, expand]
  );

  const addPlace = useCallback(() => {
    const key = newStationDetailKey();
    form.setFieldValue('stationDetails', (prev) =>
      prev.length === 0
        ? [
            makePlace(
              key,
              form.state.values.stationLatitude,
              form.state.values.stationLongitude,
              form.state.values.stationZoom
            ),
          ]
        : [...prev, makePlace(key, null, null, null)]
    );
    clearStationCoords();
    expand(key);
  }, [form, makePlace, clearStationCoords, expand]);

  const moveToTop = useCallback(
    (index: number) => {
      form.setFieldValue('stationDetails', (prev) => {
        if (index <= 0 || index >= prev.length) return prev;
        const arr = [...prev];
        const [item] = arr.splice(index, 1);
        arr.unshift(item);
        return arr;
      });
    },
    [form]
  );

  const removePlace = useCallback(
    (index: number) => {
      form.setFieldValue('stationDetails', (prev) => prev.filter((_, i) => i !== index));
    },
    [form]
  );

  // Drag & drop přeřazení nástupních míst (pořadí = Ord, 1. = hlavní = GPS stanice).
  const reorder = useCallback(
    (from: number, to: number) => {
      if (from === to) return;
      form.setFieldValue('stationDetails', (prev) => {
        const arr = [...prev];
        const [item] = arr.splice(from, 1);
        arr.splice(to, 0, item);
        return arr;
      });
    },
    [form]
  );

  const setCoord = useCallback(
    (name: string, raw: string) => {
      const trimmed = raw.trim();
      const value = trimmed === '' ? null : Number(trimmed);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamická cesta pole; hodnota number|null je validní.
      (form.setFieldValue as any)(name, value);
    },
    [form]
  );

  return {
    mapMode,
    setMapMode,
    fitSignal,
    fit,
    dragIndex,
    setDragIndex,
    dragOverIndex,
    setDragOverIndex,
    expandedKey,
    toggleExpand,
    handleMapClick,
    addPlace,
    moveToTop,
    removePlace,
    reorder,
    setCoord,
  };
}

/** Typ editoru nástupních míst (pro propsy podřízených komponent detailu stanice). */
export type PlaceEditor = ReturnType<typeof usePlaceEditor>;
